import { useIsMutating } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Button, Container
} from "reactstrap";
import { triggerCheckBusinessRules } from "../../../../actions/Checks/actions";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useCurrentCheck, useCurrentLayer } from "../../../../hooks/useCurrentEntities";
import { useConnectivity } from "../../../../offline/connectivityMonitor";
import { isOfflineChecksEnabled } from "../../../../offline/db";
import LayerBreadcrumbs from "../../Layers/Layer/LayerBreadcrumbs";
import { PageHeader } from "../../Layout/PageHeader";
import useCheckFlowNavigate from "../useCheckFlowNavigate";

export interface BoxProps {
    buttons?: any;
    hideButtons?: boolean;
    canSubmit?: boolean;
    canGoBack?: boolean;
    submit?: () => boolean;
    extra_buttons?: any[];
    children?: any;
    devicesModalButton?: any;
}

export default function Box({
    children,
    hideButtons,
    canSubmit = true,
    canGoBack = true,
    submit,
    extra_buttons = [],
    devicesModalButton,
    ...props
}: BoxProps) {
    const buttons = hideButtons ? null : (
        <>{props.buttons ?? <BottomNavButtons canSubmit={canSubmit} canGoBack={canGoBack} submit={submit} extra_buttons={extra_buttons} />}</>
    );

    return (
        <Container>
            <Header devicesModalButton={devicesModalButton} />

            {buttons}

            <div className={`bg-body rounded shadow p-2 p-lg-4 ${buttons ? "my-2 my-md-4" : ""}`}>
                {children}
            </div>

            {buttons}
        </Container>
    );
}

export interface BottomNavButtonsProps {
    canSubmit?: boolean;
    canGoBack?: boolean;
    submit?: () => boolean;
    extra_buttons?: any[];
}

export function BottomNavButtons({ submit, canSubmit = true, canGoBack = true, extra_buttons = [] }: BottomNavButtonsProps) {
    const params = useParams();
    // No mutation dispatches avocados.isUpdating anymore; a pending fruit mutation is the equivalent signal.
    const isUpdating = useIsMutating() > 0;
    const config = useConfig();
    const { isOnline } = useConnectivity();
    const inOfflineMode = isOfflineChecksEnabled(config) && !isOnline;
    const { data: check } = useCurrentCheck(params.tenant_id, Number(params.check_id), !inOfflineMode);
    const { data: layer } = useCurrentLayer(params.tenant_id, params.layer_id, !inOfflineMode);
    const location = config.get_check_type_config(layer, check);
    const dispatch = useDispatch();
    const { navigateToCheckAction, exitCheckFlow } = useCheckFlowNavigate();

    if (!check.location || !layer.type) {
        return null;
    }

    const next = config.next_check_page(layer, check, params.action as string);
    const prev = config.prev_check_page(layer, check, params.action as string);

    const nextOrSubmit = () => {
        if (!canSubmit) {
            toast.error("Please complete all required fields before proceeding.");
            return;
        }

        if (next) {
            navigateToCheckAction(next.path);
            return;
        }
        if (submit && !submit()) {
            // *Stop navigating if the submit function returns false
            return;
        }

        // * no next, no falsy custom submit, then trigger business rules and navigate back
        dispatch(triggerCheckBusinessRules(params.tenant_id as string, check.check_id) as any);

        // * Show Call to action from before leaving (if configured)
        if (location?.cta_form_title) {
            navigateToCheckAction(`${params.action}/modal/cta`);
            return;
        }

        exitCheckFlow();
    };

    return (
        <div className="d-md-flex justify-content-end align-items-center gap-2">
            {prev && (
                <div className="d-inline-flex d-md-block">
                    <Button color="secondary" outline={true} disabled={isUpdating || !canGoBack} onClick={() => navigateToCheckAction(prev.path)}>Back</Button>
                </div>
            )}

            {extra_buttons.map((i, index) => <div key={index}>{i}</div>)}

            <div className="d-inline-flex d-md-block">
                <Button color="primary" disabled={isUpdating || !canSubmit} onClick={nextOrSubmit}>{next ? "Next" : "Finish"}</Button>
            </div>
        </div>
    );
}

type HeaderProps = {
    name?: string;
    devicesModalButton?: any;
};

export const Header = (props: HeaderProps) => {
    const { devicesModalButton } = props;
    const config = useConfig();
    const { isOnline } = useConnectivity();
    const inOfflineMode = isOfflineChecksEnabled(config) && !isOnline;
    const params = useParams();
    const { data: check } = useCurrentCheck(params.tenant_id, Number(params.check_id), !inOfflineMode);
    const { data: layer } = useCurrentLayer(params.tenant_id, params.layer_id, !inOfflineMode);

    const location_config = config.get_check_type_config(layer, check);
    const human_check = check.location ? location_config?.text : false;
    const layer_config = config.get_layer_config(layer);

    return (
        <PageHeader
            breadcrumbs={<LayerBreadcrumbs check={check} />}
            title={check.group_label ? check.group_label : `${layer_config?.text} ${layer.label}${human_check ? ` ${human_check}` : ""}`}
        >
            {props.name && <h3>{props.name}</h3>}
            {devicesModalButton}
        </PageHeader>
    );
};

type AdvanceRipeningLayoutProps = {
    ripeningDays?: any;
    children?: any;
    buttons?: any;
    hideButtons?: boolean;
};

export const AdvanceRipeningLayout = (props: AdvanceRipeningLayoutProps) => {
    const { children, ripeningDays, buttons, hideButtons } = props;

    const buttonsComp = (
        <Container>
            {!hideButtons && buttons && <div>{buttons}</div>}
        </Container>
    );

    return (
        <div>
            <div>
                <div className="px-3 py-4">
                    <Container>
                        <Header />
                    </Container>
                </div>

                {ripeningDays}

                {buttonsComp}
            </div>

            <Container>
                <div className="bg-body rounded shadow">
                    <div className="p-lg-5 p-2" >
                        {children}
                    </div>
                </div>
            </Container>

            {buttonsComp}
        </div>
    );
};
